import { AlmaAccountCore } from '@almamedia/alma-account-core';
import { AlmaAccountAnalytics } from '@almamedia/alma-account-analytics';

const almaTunnusParametersElement = $('#almaTunnusParameters');
const almaTunnus = {
    allowedAuth0ClientIds: Object.values(almaTunnusParametersElement.data('alma-tunnus-allowed-auth0-client-ids')),
    auth0ClientId: almaTunnusParametersElement.data('alma-tunnus-auth0-client-id'),
    domain: almaTunnusParametersElement.data('alma-tunnus-domain'),
    audience: almaTunnusParametersElement.data('alma-tunnus-audiance'),
    siteLanguage: almaTunnusParametersElement.data('alma-tunnus-site-language'),
    realm: 'Almatunnus',
    tokenName: 'alma-token',
    redirectUrl: window.location.href,
    state: '',
    almaProcessLoader: $('.almaProcessLoader'),
    source: '',
    autoLoginNotificationCookie: 'show-auto-login-notification',
    autoLoginPopupTimeout: 14 * 24 * 60, // 14 days
    autoLoginPopupDisplayDuration: 3000, // 3 seconds
    ajaxWaitTimeout: 5000, // 5 seconds
    autoLoginTimeout: 1500, // 1.5 seconds
    async initAlmaTunnusCore() {
        try {
            // Initialize core using the installed @almamedia/alma-account-core package
            this.core = AlmaAccountCore.getInstance();

            await this.core.init({
                domain: this.domain,
                clientId: this.auth0ClientId,
                redirectUri: Routing.generate('alma_callback'),
                realm: 'Almatunnus',
                audience: this.audience,
                scope: 'openid profile email offline_access',
            });

            return this.core;
        } catch (error) {
            console.error('AlmaTunnusCoreError:', error);
        }
        return undefined;
    },
    async loadAlmaAccountAnalyticsCore() {
        try {
            window.AlmaAccountAnalytics = AlmaAccountAnalytics.getInstance({
                clientId: this.auth0ClientId,
            });
        } catch (error) {
            console.error(`Error loading AlmaAccountAnalytics for ${this.environment}:`, error);
        }
    },
    async sendUserEvent(eventType) {
        try {
            const almaUser = await this.getAlmaUser();
            if (almaUser) {
                const eventData = { user: almaUser };
                if (eventType === 'autologin') {
                    eventData.source = 'autologin';
                }
                await window.AlmaAccountAnalytics.sendUserEvent(eventData);
            }
        } catch (error) {
            console.error('Error sending user event:', error);
            // You can also add custom error handling logic here if needed.
        }
    },
    async sendLogoutEvent() {
        try {
            const token = await this.getIdToken();
            if (typeof token !== 'undefined') {
                const almaIDHash = token['https://almamedia.net/almaIDHash'] || '';
                const connection = token['https://almamedia.net/connection'];

                await window.AlmaAccountAnalytics.sendLogout({ almaIDHash, connection, source: 'logout-button' });
            }
        } catch (error) {
            console.error('Error during logout event:', error);
            // Add any additional error handling logic if needed (e.g., showing a message to the user)
        }
    },
    async checkAuthentication() {
        try {
            return await this.core.isAuthenticated();
        } catch (error) {
            console.error('Error during authentication check:', error);
            return false;
        }
    },
    async loginUser(idToken) {
        try {
            if (idToken) {
                const user = JSON.parse(JSON.stringify(idToken));
                if (user) {
                    if (user.email_verified) {
                        await this.setSessionActive(this.domain, this.auth0ClientId);
                        commonUtils.setCookie(this.tokenName, user.__raw, 1);
                        this.verifyMapAlmaUser(user, this.redirectUrl, false);
                    } else {
                        if (!Object.prototype.hasOwnProperty.call(user, 'https://almamedia.net/fromSignUp')) {
                            try {
                                const verifyEmailUrl = await window.ALMA.account.selfServiceVerifyEmailUrl();
                                // eslint-disable-next-line no-void
                                void fetch(verifyEmailUrl)
                                    .then((response) => {
                                        if (!response.ok) {
                                            throw new Error(
                                                `Verify email request failed with status ${response.status}`
                                            );
                                        }
                                    })
                                    .catch((error) => {
                                        console.error('Error fetching verifyEmailUrl:', error);
                                    });
                            } catch (error) {
                                console.error('Error fetching verifyEmailUrl:', error);
                            }
                        }
                        this.almaProcessLoader.hide();
                        this.openUnverifiedAlmaUserModal(user.email);
                    }
                } else {
                    window.location.href = this.redirectUrl;
                }
            }
        } catch (error) {
            console.log(`login callback error: ${error.message}`);
            almaTunnus.setFlashMessage('problem_case_login_failed');
        }
    },
    async loginCallback() {
        try {
            const isAuthenticated = await this.checkAuthentication();
            if (isAuthenticated) {
                this.almaProcessLoader.show();
                const token = await this.core.getIdToken();
                await this.sendUserEvent();
                await this.loginUser(token);
            } else if (!commonUtils.isUserLogin() && !commonUtils.isDealerLogin()) {
                const accessToken = await this.getAccessToken(false);
                if (accessToken) {
                    commonUtils.setCookie(this.autoLoginNotificationCookie, 'no', this.autoLoginPopupTimeout);
                    this.almaProcessLoader.show();

                    this.core.checkSession();
                    const token = await this.core.getIdToken();
                    await this.sendUserEvent();
                    await this.loginUser(token);
                }
            }
        } catch (error) {
            console.error('Login attempt failed:', error);
        } finally {
            setTimeout(() => {
                this.almaProcessLoader.hide(); // Delaying the hide action for debugging
            }, this.autoLoginPopupDisplayDuration);
        }
    },
    isAutoLoginEnabled() {
        return commonUtils.getCookie(this.autoLoginNotificationCookie) === 'yes';
    },
    showAutoLoginNotificationPopup() {
        if (!this.isAutoLoginEnabled()) return;
        $('#autologinPopupContainer').addClass('relative');
        $('#autoLoginPopup').fadeIn();

        setTimeout(() => this.hideAutoLoginPopup(), this.autoLoginPopupDisplayDuration);
    },
    hideAutoLoginPopup() {
        $('#autoLoginPopup').fadeOut().hide();
        $('#autologinPopupContainer').removeClass('relative');
        commonUtils.setCookie(this.autoLoginNotificationCookie, 'no', this.autoLoginPopupTimeout);
    },
    async verifyUserSession() {
        if (commonUtils.isDealerLogin()) {
            return;
        }

        const isAuthenticated = await this.checkAuthentication();
        if (commonUtils.isUserLogin() && !isAuthenticated) {
            console.log('SSO session exists, but Alma session does not.');
            const result = await this.getAccessToken(true);

            if (result) {
                if (commonUtils.getCookie(this.autoLoginNotificationCookie) === null) {
                    commonUtils.setCookie(this.autoLoginNotificationCookie, 'yes', this.autoLoginPopupTimeout);
                }
                this.showAutoLoginNotificationPopup();
                console.log('Alma session created.');
                await this.core.checkSession();
                await this.sendUserEvent('autologin');

                // Check and publish UPS auth event for autologin users
                await almaTunnus.publishUpsAuthEventForCurrentUser();
            }
            return;
        }

        if (!commonUtils.isUserLogin() && isAuthenticated) {
            console.log('User is logged out, but Alma cache exists.');
            await this.clearAuth0LocalStorage();
            const result = await this.getAccessToken(false);

            if (result) {
                console.log('Alma session exists but no SSO session.');
                await this.core.checkSession();
                await this.sendUserEvent();
                const token = await this.core.getIdToken();
                await this.loginUser(token);
            } else {
                console.log('Alma side session deleted.');
            }
        }

        if ($('#colorbox').is(':visible') && $('#cboxLoadedContent').find('#ajoLoginModal').length > 0) {
            $.colorbox.close();
        }
    },
    verifyMapAlmaUser(almaUser, redirectUrlInput, callTos) {
        const redirectUrl = String(redirectUrlInput);

        // prevent below logic checking when user is already login
        if (commonUtils.isUserLogin()) {
            window.location.href = redirectUrl;
        }
        $.ajax({
            type: 'POST',
            async: false,
            url: Routing.generate('verify_map_alma_user'),
            data: { almaToken: almaUser.__raw, isTos: callTos },
            async success(response) {
                if (response.status === 200) {
                    // Check and publish UPS auth event for autologin users
                    await almaTunnus.publishUpsAuthEventForCurrentUser();
                    window.location.href = `/sso/login?successUrl=${encodeURIComponent(redirectUrl)}&failureUrl=${encodeURIComponent(redirectUrl)}`;
                } else {
                    let { data } = response;
                    if (typeof data.tos !== 'undefined') {
                        almaTunnus.silentRegistrationForOutsideNettixUser(almaUser.email, redirectUrl);
                    } else {
                        commonUtils.setCookie('alma-login-error', data.error, 5);
                        almaTunnus.almaLogout();
                    }
                }
            },
        });
    },
    async publishUpsAuthEventForCurrentUser() {
        try {
            const almaUser = await this.getAlmaUser();
            if (almaUser) {
                const almaId = almaUser['https://almamedia.net/site_id'] || '';
                if (almaId) {
                    await this.checkAndPublishUpsAuthEvent(almaId);
                } else {
                    console.log('UPS EventBus : Skip UPS auth event: No almaId found in user data');
                }
            }
        } catch (error) {
            console.error('UPS EventBus : Error getting alma user for UPS auth event:', error);
        }
    },
    async checkAndPublishUpsAuthEvent(almaId) {
        if (!almaId) {
            console.log('UPS EventBus : Skip UPS auth event: No almaId');
            return;
        }

        // Check if user already has UPS auth entry for this site
        try {
            const response = await $.ajax({
                type: 'POST',
                url: Routing.generate('publish_ups_auth_event'),
                data: { almaId },
                timeout: almaTunnus.ajaxWaitTimeout,
            });

            if (response.status === 'error') {
                console.error('UPS EventBus : UPS auth event failed', response);
            } else {
                console.log('UPS EventBus : UPS auth event success');
            }
        } catch (error) {
            console.error('UPS EventBus : Failed to publish UPS auth event:', error);
        }
    },
    async almaLogout() {
        commonUtils.setCookie('nettix-user-groups', 'user,company', 1);
        commonUtils.deleteCookie(this.tokenName);
        commonUtils.deleteCookie(this.autoLoginNotificationCookie);

        if (!commonUtils.isDealerLogin()) {
            try {
                await this.removeSessionData(this.domain);
                this.clearClientLocalStorage();
                await this.sendLogoutEvent();
                this.coreLogout();
            } catch (error) {
                await this.sendLogoutEvent();
                this.coreLogout();
                console.log(`logout error: ${error.message}`);
            }
        } else {
            window.location.href = `/sso/logout?successUrl=${encodeURIComponent(Routing.generate('logout'))}&failureUrl=${encodeURIComponent(Routing.generate('logout'))}`;
        }
    },
    coreLogout() {
        this.core.logout(Routing.generate('logout'));
    },
    async getIdToken() {
        return this.core.getIdToken();
    },
    async clearAuth0LocalStorage() {
        return new Promise((resolve, reject) => {
            try {
                // Iterate backward through localStorage
                for (let i = localStorage.length - 1; i >= 0; i--) {
                    const key = localStorage.key(i);
                    if (key?.startsWith('@@auth0spajs@@')) {
                        localStorage.removeItem(key);
                    }
                }
                resolve(); // Resolve when done
            } catch (error) {
                reject(error); // Reject if there's an error
            }
        });
    },
    async getAccessToken(checkPopuptoken) {
        try {
            let accessToken = '';

            if (checkPopuptoken) {
                accessToken = await this.core.silentAuthentication();
                console.log('Silent authentication token :', !!accessToken);
                if (accessToken !== undefined) {
                    return accessToken;
                }
            }

            // fetch new token
            accessToken = await this.core.silentAuthentication({ cacheMode: 'off' });
            console.log('Silent authentication token with cache disabled :', !!accessToken);
            if (accessToken !== undefined) {
                return accessToken;
            }

            // finally load from popup
            if (checkPopuptoken) {
                return true;
            }

            return accessToken;
        } catch (error) {
            this.almaProcessLoader.hide();
            console.error('Error fetching access token:', error);
        }
        return undefined;
    },
    async getAlmaUser() {
        return this.core.getUserInfo();
    },
    clearClientLocalStorage() {
        console.log('No Ajo sessions, clearing local storage');
        localStorage.removeItem('ALMA');
        localStorage.removeItem('almad-gravito_microprofile');
        commonUtils.deleteCookie('auth0');
        commonUtils.deleteCookie('auth0.ssodata');
        commonUtils.deleteCookie('_auth0.ssodata_compat');

        // Remove local storage items related to drafts and messages in Inbox
        localStorage.removeItem('draft-storage');
        localStorage.removeItem('message-storage');
    },
    isEmptyObject(obj) {
        // Added missing obj parameter
        return Object.keys(obj).length === 0;
    },
    generateState() {
        const state = crypto.randomUUID();

        this.state = state;

        return state;
    },
    setFlashMessage(message) {
        let result = false;
        $.ajax({
            type: 'GET',
            url: Routing.generate('alma_tunnus_set_flash_message'),
            async: false,
            data: `message=${message}`,
            success(responseRaw) {
                if (responseRaw !== 'null' && responseRaw.length > 0) {
                    const response = $.parseJSON(responseRaw);
                    result = response.success;
                }
            },
        });

        return result;
    },
    openUnverifiedAlmaUserModal(almaEmail) {
        $.colorbox({
            inline: true,
            href: '#unverifiedAlmaUserModal',
            overlayClose: false,
            onOpen() {
                $('#unverifiedAlmaEmailAddress').html(almaEmail);
            },
            onClosed() {
                almaTunnus.almaLogout(true);
            },
        });
    },
    async silentRegistrationAlmaUser(url) {
        try {
            const token = await almaTunnus.getIdToken();
            const almaUser = JSON.parse(JSON.stringify(token));

            almaTunnus.verifyMapAlmaUser(almaUser, url, true);
        } catch (error) {
            console.error('Error fetching token:', error);
        }
    },
    silentRegistrationForOutsideNettixUser(email, originUrl) {
        if (email) {
            $('#autologinEmail').text(email);
            this.silentRegistrationAlmaUser(originUrl);
            commonUtils.setCookie(this.autoLoginNotificationCookie, 'yes', this.autoLoginPopupTimeout);
            this.showAutoLoginNotificationPopup(true);
        }
    },
    openTeaserModalDialog(onContinue) {
        $.colorbox({
            inline: true,
            href: '#teaserModal',
            onLoad() {
                $('html').css({ overflow: '' });
            },
            onComplete() {
                $('#teaserModal .continue').on('click', function (e) {
                    e.preventDefault();
                    $.colorbox.close();
                    if (typeof onContinue === 'function') {
                        onContinue();
                    }
                });

                $('#teaserModal .cancel').on('click', function () {
                    $.colorbox.close();
                });
            },
            onClosed() {
                const doNotShow = $('#teaserModal #doNotShowAgain').is(':checked');
                if (doNotShow) {
                    try {
                        localStorage.setItem('almaTeaserDoNotShow', 'true');
                    } catch (e) {
                        console.warn('Unable to save teaser preference to localStorage:', e);
                    }
                }
                $('#teaserModal .continue').off('click');
                $('#teaserModal .cancel').off('click');
            },
        });
    },
    async checkActiveSession(domain) {
        return new Promise((resolve) => {
            const uniqueString = Math.random().toString(36).substr(2, 9);
            const iframeId = `session-check-${Date.now()}-${uniqueString}`;
            const iframe = document.createElement('iframe');
            iframe.id = iframeId;
            iframe.style.display = 'none';
            iframe.src = `https://assets.${domain}/cookies/sessionCheck.html`;

            let isResolved = false;

            function handleMessage(event) {
                if (isResolved) return;

                // Always validate the origin!
                if (event.origin !== `https://assets.${domain}`) return;

                if (event.data === 'at-session-active') {
                    cleanup();
                    resolve(true);
                } else if (event.data === 'at-session-inactive') {
                    cleanup();
                    resolve(false);
                }
            }

            function cleanup() {
                if (isResolved) return;
                isResolved = true;

                window.removeEventListener('message', handleMessage);
                const iframeElement = document.getElementById(iframeId);
                if (iframeElement && iframeElement.parentNode) {
                    iframeElement.parentNode.removeChild(iframeElement);
                }
            }

            function handleError() {
                cleanup();
                resolve(false);
            }

            window.addEventListener('message', handleMessage);
            iframe.onerror = handleError;

            // Timeout fallback
            setTimeout(() => {
                if (!isResolved) {
                    cleanup();
                    resolve(false);
                }
            }, this.autoLoginTimeout);

            try {
                document.body.appendChild(iframe);
            } catch (error) {
                console.log('Error appending iframe:', error);
                handleError();
            }
        });
    },
    async setSessionActive(domain) {
        return new Promise((resolve, reject) => {
            const uniqueString = Math.random().toString(36).substr(2, 9);
            const iframeId = `session-set-${Date.now()}-${uniqueString}`;
            const iframe = document.createElement('iframe');
            iframe.id = iframeId;
            iframe.style.display = 'none';
            iframe.src = `https://assets.${domain}/cookies/sessionCheck.html?client_id=${encodeURIComponent(this.auth0ClientId)}#set`;

            let isResolved = false;

            function handleMessage(event) {
                if (isResolved) return;

                if (event.origin !== `https://assets.${domain}`) return;
                if (event.data === 'at-session-active') {
                    cleanup();
                    resolve();
                }
            }

            function cleanup() {
                if (isResolved) return;
                isResolved = true;

                window.removeEventListener('message', handleMessage);
                const iframeElement = document.getElementById(iframeId);
                if (iframeElement && iframeElement.parentNode) {
                    iframeElement.parentNode.removeChild(iframeElement);
                }
            }

            function handleError(error) {
                cleanup();
                reject(error);
            }

            window.addEventListener('message', handleMessage);
            iframe.onerror = () => handleError(new Error('Failed to load session iframe'));

            // Always clean up after timeout
            setTimeout(() => {
                if (!isResolved) {
                    cleanup();
                    resolve(); // Consider this successful even without response
                }
            }, this.autoLoginTimeout);

            try {
                document.body.appendChild(iframe);
            } catch (error) {
                console.log('Error appending iframe:', error);
                handleError(error);
            }
        });
    },
    async removeSessionData(domain) {
        return new Promise((resolve) => {
            const uniqueString = Math.random().toString(36).substr(2, 9);
            const iframeId = `session-remove-${Date.now()}-${uniqueString}`;
            const iframe = document.createElement('iframe');
            iframe.id = iframeId;
            iframe.style.display = 'none';
            iframe.src = `https://assets.${domain}/cookies/sessionCheck.html#remove`;

            function cleanup() {
                const iframeElement = document.getElementById(iframeId);
                if (iframeElement && iframeElement.parentNode) {
                    iframeElement.parentNode.removeChild(iframeElement);
                }
                resolve();
            }

            iframe.onerror = cleanup;

            // Clean up after delay regardless of response
            setTimeout(cleanup, this.autoLoginTimeout);

            try {
                document.body.appendChild(iframe);
            } catch (error) {
                console.log('Error appending iframe:', error);
                resolve();
            }
        });
    },
};

export { almaTunnus };

export const almaTunnusLogin = {
    async init(sourceInput, redirectToRegisterInput, redirectUrlInput) {
        const source = sourceInput || '';
        const redirectUrl = redirectUrlInput || window.location.href;
        const redirectToRegister = redirectToRegisterInput || false;
        let screenHint = redirectToRegister ? 'signup' : 'login';
        const { state } = almaTunnus;
        const MAX_RETRIES = 3;
        const RETRY_DELAY = 2000;
        almaTunnus.redirectUrl = redirectUrl;

        if (commonUtils.isUserLogin()) {
            window.location.href = redirectUrl;
            return false;
        }

        // If user said "Do not show again", go directly to login
        let skipTeaser = false;
        try {
            skipTeaser = localStorage.getItem('almaTeaserDoNotShow') === 'true';
        } catch (e) {
            console.warn('Unable to read teaser preference from localStorage:', e);
        }
        if (skipTeaser) {
            tryPopupLogin();
        } else if ($('#colorbox').is(':visible')) {
            // Show teaser, then login only if user continues
            $(document).one('cbox_closed', () => {
                almaTunnus.openTeaserModalDialog(() => tryPopupLogin());
            });
            $.colorbox.close();
        } else {
            almaTunnus.openTeaserModalDialog(() => tryPopupLogin());
        }

        commonUtils.setCookie(state, JSON.stringify({ redirectUrl }), 1);

        async function tryPopupLogin(attempt = 1) {
            try {
                if (!almaTunnus.core) {
                    throw new Error('Core is not ready');
                }

                await almaTunnus.core
                    .popupLogin({
                        authorizationParams: {
                            theme: 'light',
                            lang: almaTunnus.siteLanguage,
                            source,
                            screen_hint: screenHint,
                        },
                    })
                    .then(() => almaTunnus.loginCallback());
            } catch (error) {
                console.error(`Attempt ${attempt} failed:`, error);

                if (attempt < MAX_RETRIES) {
                    console.log(`Retrying in ${RETRY_DELAY / 1000} seconds...`);
                    setTimeout(() => tryPopupLogin(attempt + 1), RETRY_DELAY);
                } else {
                    console.error('Maximum retry attempts reached. Login failed.');
                    alert('Login failed after multiple attempts. Please try again later.');
                }
            }
        }
        return undefined;
    },
};

const forgotPassword = {
    forgotPasswordForm: $('#forgot_password_form'),
    passwordResetUrl: almaTunnusParametersElement.data('alma-tunnus-password-reset-url'),
    auth0ClientId: almaTunnusParametersElement.data('alma-tunnus-auth0-client-id'),

    openForgotPasswordModal() {
        window.$.colorbox({
            inline: true,
            href: '#forgot_password_popup',
            overlayClose: false,
            onClosed() {
                const $forgotPasswordForm = $('#forgot_password_form');
                $forgotPasswordForm.find('.global-validation-msg').text('').hide();
                $forgotPasswordForm[0].reset();
            },
        });
    },
    submitFormEvent() {
        let emailId = this.forgotPasswordForm.find('.emailid-field').val();
        if (commonUtils.isEmpty(emailId)) {
            this.errorMessage = Translations.blank_form_submit_text;
            this.forgotPasswordForm.find('.global-validation-msg').text(this.errorMessage).show();
            window.$.colorbox.resize();
        } else if (!commonUtils.isValidEmail(emailId)) {
            this.errorMessage = Translations.invalid_email;
            this.forgotPasswordForm.find('.global-validation-msg').text(this.errorMessage).show();
            window.$.colorbox.resize();
        } else {
            let params = {
                clientId: this.auth0ClientId !== '' ? this.auth0ClientId : '',
                email: emailId,
            };
            this.forgotPasswordForm.find('.global-validation-msg').text('').hide();
            window.location.href = `${this.passwordResetUrl}?${$.param(params)}`;
        }

        return false;
    },
};

$(function () {
    async function initializeAlmaTunnusAuthentication() {
        const core = await almaTunnus.initAlmaTunnusCore();
        await almaTunnus.loadAlmaAccountAnalyticsCore();
        console.log('AlmaTunnus Core loaded successfully.');

        almaTunnus.generateState();

        try {
            if (!commonUtils.isUserLogin()) {
                const hasActiveSession = await almaTunnus.checkActiveSession(almaTunnus.domain);
                console.log('hasActiveSession:', hasActiveSession);
                if (hasActiveSession) {
                    const user = await core.autologin();

                    if (user) {
                        almaTunnus.almaProcessLoader.show();
                        await window.AlmaAccountAnalytics.sendUserEvent({ user, source: 'autologin' });
                        const token = await almaTunnus.getIdToken();

                        commonUtils.setCookie(
                            almaTunnus.autoLoginNotificationCookie,
                            'yes',
                            almaTunnus.autoLoginPopupTimeout
                        );
                        await almaTunnus.loginUser(token);
                    }
                } else {
                    commonUtils.deleteCookie(almaTunnus.autoLoginNotificationCookie);
                }
            } else if (almaTunnus.isAutoLoginEnabled()) {
                almaTunnus.showAutoLoginNotificationPopup();
            }
        } catch (error) {
            console.error('Third-party autologin failed:', error);
        }

        try {
            await almaTunnus.verifyUserSession();
            console.log('User session verified successfully.');
        } catch (error) {
            console.error('User session verification failed:', error);
        }

        const $body = $('body');

        $body.on('click', '.logout', function () {
            almaTunnus.almaLogout();
        });

        $body.on('click', '#customerServiceLink a', async function (event) {
            event.preventDefault();
            almaTunnus.almaProcessLoader.show();
            try {
                const verifyEmailUrl = await window.ALMA.account.selfServiceVerifyEmailUrl();
                // eslint-disable-next-line no-void
                void fetch(verifyEmailUrl)
                    .then((response) => {
                        if (!response.ok) {
                            throw new Error(`Verify email request failed with status ${response.status}`);
                        }
                    })
                    .catch((error) => {
                        console.error('Error fetching verifyEmailUrl:', error);
                    });
                commonUtils.setCookie('alma-login-error', Translations.alma_tunnus.verification_email_sent, 5);
                window.$.colorbox.close();
            } catch (error) {
                console.error('Error fetching verifyEmailUrl:', error);
            }
            almaTunnus.almaProcessLoader.hide();
        });

        // open forgot password popup
        $body.on('click', '.forgot_password_link', function () {
            forgotPassword.openForgotPasswordModal();
        });

        $body.on('click', '.submitForgotPasswordButton', function (e) {
            e.preventDefault();
            forgotPassword.submitFormEvent();
        });

        // alma register event link
        $body.on('click', '.almaRegisterEvent', function () {
            let eventName = $(this).data('event-name');
            almaTunnusLogin.init(eventName, true);
        });

        // alma Login event link
        $body.on('click', '.almaLoginEvent', function () {
            let eventName = $(this).data('event-name');
            almaTunnusLogin.init(eventName);
        });
    }

    // if dealer login page then do not initialize alma tunnus
    if (!window.location.href.includes('businesslogin')) {
        initializeAlmaTunnusAuthentication();
    }
});

// Event delegation handler for data-action-alma-login
$(document).on('click', '[data-action-alma-login]', function (e) {
    e.preventDefault();
    let source = $(this).data('action-alma-login');
    let redirectToRegister = $(this).data('param-alma-login-register') || false;
    let redirectUrl = $(this).data('param-alma-login-redirect') || undefined;
    almaTunnusLogin.init(source, redirectToRegister, redirectUrl);
});
